import { DateTime } from 'luxon';
import { SearchTermDomain } from '../../../__graphqlTypes/globalTypes';
import { SentryService } from '../common/sentryService';
import { GraphqlService } from '../graphql/GraphqlService';
import { RecordSearchTerm, RecordSearchTermVariables } from './__graphqlTypes/RecordSearchTerm';
import { RECORD_SEARCH_TERM } from './record-search-term.mutation';

export type RecordLearnSearchTermOptions = {
    clientId: string;
    locationId: string;
    // The exact search total from the first page of results, null when unknown such as when the search failed
    resultCount: number | null;
    searchTerm: string;
};

/**
 * Records submitted free text search terms from the legacy Learn pages for search term reporting.
 * Called once the first page of search results has loaded, so that the exact search total can be included.
 */
export class SearchTermReportingService {
    public static readonly SID = 'SearchTermReportingService';

    public static readonly $inject: string[] = [GraphqlService.SID, SentryService.SID];

    public constructor(
        private readonly graphqlService: GraphqlService,
        private readonly sentryService: SentryService,
    ) {}

    /**
     * Fire and forget so that recording never blocks or breaks the search experience.
     * Failures are reported to Sentry instead of being surfaced to the user.
     * Empty and whitespace only search terms are not recorded.
     */
    public recordLearnSearchTerm({
        clientId,
        locationId,
        resultCount,
        searchTerm,
    }: RecordLearnSearchTermOptions): void {
        if (searchTerm.trim() === '') {
            return;
        }

        void this.graphqlService
            .getClient()
            .mutate<RecordSearchTerm, RecordSearchTermVariables>({
                mutation: RECORD_SEARCH_TERM,
                variables: {
                    input: {
                        browser: window.navigator.userAgent,
                        clientId,
                        domain: SearchTermDomain.LEARN,
                        locationId,
                        resultCount,
                        searchTerm,
                        timestamp: DateTime.utc().toString(),
                        timezone: DateTime.now().zoneName ?? '',
                    },
                },
            })
            .then(({ data }) => {
                if (data?.recordSearchTerm.__typename === 'RecordSearchTermError') {
                    this.reportFailure(new Error(`${data.recordSearchTerm.code}: ${data.recordSearchTerm.message}`));
                }
            })
            .catch((error: unknown) => this.reportFailure(error));
    }

    private reportFailure(error: unknown): void {
        this.sentryService.captureException('Failed to record search term', error);
    }
}

angular.module('app').service(SearchTermReportingService.SID, SearchTermReportingService);
